import { type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, unref, type Ref } from 'vue';

import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { searchSuppliers } from '#domain/core/filter/filter.query';
import { transformFilterFromSearch } from '#domain/core/filter/filter.utilities';
import { useUserStore } from '#stores/user.store';

interface UseSuppliersDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useSuppliersData({ debouncedSearch, enabled, checkedItemsState, taxonomy }: UseSuppliersDataProps) {
  const user = useUserStore();
  const { isAnonymized } = storeToRefs(user);

  const currentSubSuppliersFilters = computed(() => {
    return Object.entries(checkedItemsState.value.suppliersFilters)
      .filter(([, state]) => state.state === 'checked' && state.filterType === 'subSupplier')
      .map(([filter]) => filter);
  });

  const currentLocalSupplierFilters = computed(() => {
    return Object.entries(checkedItemsState.value.suppliersFilters)
      .filter(([, state]) => state.state === 'checked' && state.filterType === 'localSupplier')
      .map(([filter]) => filter);
  });

  const {
    data: suppliersData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [
      searchSuppliers.name,
      debouncedSearch,
      currentSubSuppliersFilters,
      currentLocalSupplierFilters,
      taxonomy
    ],
    queryFn: async () => {
      return searchSuppliers({
        orNodeIdIn: [...currentSubSuppliersFilters.value, ...currentLocalSupplierFilters.value],
        search: debouncedSearch.value,
        limit: 100,
        taxonomy: unref(taxonomy)
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsSuppliers = computed(() => {
    if (!suppliersData.value || !enabled.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: isAnonymized.value
        ? transformFilterFromSearch(suppliersData.value, 'Supplier / Code {level} - ******')
        : suppliersData.value,
      defaultFilters: isAnonymized.value
        ? transformFilterFromSearch(suppliersData.value, 'Supplier / Code {level} - ******')
        : suppliersData.value,
      selectedFilters: checkedItemsState.value.suppliersFilters
    });
  });

  const isLoadingSuppliers = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsSuppliers, isLoadingSuppliers };
}
